import type {
  ScaffolderStep,
  ScaffolderTask,
  ScaffolderTaskOutput,
} from '@backstage/plugin-scaffolder-react';
import type { StepLayout, StepState } from './deletionTask';
import { LEGACY_APP_TITLE } from '../shared/projectIdentity';

/**
 * What the task page of the `odoo-project` template reads from the task:
 * the layout of its steps, the facts of the project it creates — from the
 * parameters, the steps of the spec and the template's outputs —, what each
 * step does in words, and the rows of « Ce qui a été créé ». Pure:
 * `ProjectCreationTaskPage.tsx` renders it.
 *
 * The step ids, the output titles and the step inputs read here are those of
 * platform-catalog `scaffolder/odoo-project/template.yaml`. An id this page
 * does not know keeps the template's own name, in « Autres étapes ».
 */

/**
 * The step ids of the template, in its order: one row each on the page. Les
 * étapes de `CREATION_PARTS` n'ont pas de ligne à elles.
 */
export const CREATION_STEPS = [
  'check-name',
  'fetch',
  'publish',
  'branches',
  'gitlab-member',
  'lldap-groups',
  'appenv-overlays',
  'appenv-ephemeral',
  'appenv-publish',
] as const;

type CreationStepId = (typeof CREATION_STEPS)[number];

const PHASES: StepLayout['phases'] = [
  { title: 'Vérification', ids: ['check-name', 'fetch'] },
  { title: 'Dépôt GitLab', ids: ['publish', 'branches', 'gitlab-member'] },
  { title: 'Accès', ids: ['lldap-groups'] },
  {
    title: 'Environnements',
    ids: ['appenv-overlays', 'appenv-ephemeral', 'appenv-publish'],
  },
];

/**
 * Étapes du modèle affichées dans la ligne d'une autre (`StepLayout.parts`) :
 * depuis le découpage des squelettes (platform-catalog, squelette commun puis
 * fragment du type d'application), chaque rendu `fetch:template` d'un type
 * est suivi de son fragment, et la base de l'appenv précède les
 * environnements. Les tâches du modèle d'avant, sans ces étapes, gardent
 * leurs lignes telles quelles. La ligne n'est faite qu'une fois toutes ses
 * étapes faites : `createdRows` lit l'état de la ligne.
 */
export const CREATION_PARTS: Readonly<Record<string, CreationStepId>> = {
  'fetch-fragment': 'fetch',
  'appenv-base': 'appenv-overlays',
  'appenv-overlays-fragment': 'appenv-overlays',
  'appenv-ephemeral-fragment': 'appenv-ephemeral',
};

/** The short names of the steps: the template's are sentences. */
export const CREATION_STEP_NAMES: Record<CreationStepId, string> = {
  'check-name': 'Nom disponible',
  fetch: 'Squelette',
  publish: 'Dépôt',
  branches: 'Branches',
  'gitlab-member': 'Votre accès',
  'lldap-groups': 'Groupes du projet',
  'appenv-overlays': 'Environnements',
  'appenv-ephemeral': 'Branches éphémères',
  'appenv-publish': 'Publication',
};

/** The titles of the template's outputs this page reads. */
export const OUTPUT_TITLES = {
  repository: 'Dépôt GitLab',
  firstBuild: 'Suivre le premier build',
  devEnvironment: 'Environnement dev',
  next: 'Et maintenant ?',
  technical: 'Détails techniques',
} as const;

/** What the page knows of the project, each fact absent when unknown. */
export interface CreationFacts {
  project: string;
  /** `waadoo/…/apps/<projet>`: the published URL's path, else the spec's. */
  repoPath?: string;
  /** The repository's page (`publish` output `remoteUrl`, no `.git`). */
  repoUrl?: string;
  /** The repository's pipelines page, from the outputs. */
  pipelinesUrl?: string;
  /** The `dev` environment's address, from the outputs. */
  devUrl?: string;
  /** `component:default/<projet>`, from the outputs. */
  entityRef?: string;
  permanentBranches: string[];
  /** The branches open to Developers (`branches` step, `developerBranches`). */
  developerBranches: string[];
  mailCatcher?: boolean;
  /** The git commands of « Et maintenant ? », as the template writes them. */
  commands?: string;
  /** The « Détails techniques » markdown. */
  technical?: string;
  /** The name of the user who ran the task (`spec.user.ref`). */
  user?: string;
}

const isString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter(isString) : [];

/**
 * The owner group of the `publish` step's `repoUrl` input
 * (`host?owner=<groupe>&repo=${{ parameters.projectName }}`): the spec keeps
 * the step inputs as the template wrote them, rendered only when the step
 * runs. Undefined when the group itself is a template expression.
 */
function specRepoPath(task: ScaffolderTask, project: string) {
  const input = task.spec.steps.find(step => step.id === 'publish')?.input;
  const repoUrl = input?.repoUrl;
  if (!isString(repoUrl) || !repoUrl.includes('?')) return undefined;
  const owner = new URLSearchParams(repoUrl.slice(repoUrl.indexOf('?') + 1))
    .get('owner')
    ?.replace(/^\/+|\/+$/g, '');
  if (!owner || owner.includes('${{')) return undefined;
  return `${owner}/${project}`;
}

/** The path of a URL, without its slashes around; undefined if unparsable. */
function urlPath(url: string): string | undefined {
  try {
    return new URL(url).pathname.replace(/^\/+|\/+$/g, '') || undefined;
  } catch {
    return undefined;
  }
}

/**
 * The first fenced block of a markdown text that runs `git clone`: the
 * commands of the template's « Et maintenant ? ».
 */
export function cloneCommands(markdown: string): string | undefined {
  const blocks = markdown.matchAll(/```[^\n]*\n([\s\S]*?)```/g);
  for (const block of blocks) {
    const body = block[1]
      .split('\n')
      .map(line => line.trim())
      .filter(line => line !== '')
      .join('\n');
    if (body.includes('git clone ')) return body;
  }
  return undefined;
}

/** The project a creation task is about, with what is known of it. */
export function creationFacts(
  task: ScaffolderTask,
  output: ScaffolderTaskOutput | undefined,
): CreationFacts | undefined {
  const parameters = task.spec.parameters;
  const project = parameters.projectName;
  if (!isString(project)) return undefined;

  const links = output?.links ?? [];
  const linkUrl = (title: string) =>
    links.find(link => link.title === title)?.url;
  const text = (title: string) =>
    output?.text?.find(item => item.title === title)?.content;

  const facts: CreationFacts = {
    project,
    permanentBranches: strings(parameters.permanentBranches),
    developerBranches: strings(
      task.spec.steps.find(step => step.id === 'branches')?.input
        ?.developerBranches,
    ),
  };
  const repoUrl = linkUrl(OUTPUT_TITLES.repository);
  if (repoUrl) facts.repoUrl = repoUrl;
  const repoPath = (repoUrl && urlPath(repoUrl)) ?? specRepoPath(task, project);
  if (repoPath) facts.repoPath = repoPath;
  const pipelinesUrl = linkUrl(OUTPUT_TITLES.firstBuild);
  if (pipelinesUrl) facts.pipelinesUrl = pipelinesUrl;
  const devUrl = linkUrl(OUTPUT_TITLES.devEnvironment);
  if (devUrl) facts.devUrl = devUrl;
  const entityRef = links.find(link => link.entityRef)?.entityRef;
  if (entityRef) facts.entityRef = entityRef;
  if (typeof parameters.mailCatcher === 'boolean') {
    facts.mailCatcher = parameters.mailCatcher;
  }
  const next = text(OUTPUT_TITLES.next);
  const commands = next ? cloneCommands(next) : undefined;
  if (commands) facts.commands = commands;
  const technical = text(OUTPUT_TITLES.technical);
  if (technical) facts.technical = technical;
  const userRef = task.spec.user?.ref;
  if (isString(userRef)) facts.user = userRef.replace(/^.*[:/]/, '');
  return facts;
}

/** « dev, preprod et prod ». */
export function frenchList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/** The groups the `lldap-groups` step creates (`lldapProjectGroupsCreate.ts`). */
export const projectGroups = (project: string) => ({
  readers: `prj-${project}`,
  editors: `prj-${project}-editors`,
});

/**
 * The layout of the timeline: the four phases, the short names, the grey
 * line under each — the repository path and the branches when the task
 * says them.
 */
export function creationLayout(facts: CreationFacts | undefined): StepLayout {
  const groups = facts ? projectGroups(facts.project) : undefined;
  const branches = facts?.permanentBranches ?? [];
  const details: Record<string, string> = {
    'check-name': 'unique dans GitLab et le catalogue',
    fetch: 'code, chart Helm, fiche catalogue',
    'gitlab-member': 'Developer sur le dépôt',
    'appenv-ephemeral': 'modèle des branches dev-*',
    'appenv-publish': 'dépôt appenv, pris en charge par ArgoCD',
  };
  if (facts?.repoPath) details.publish = facts.repoPath;
  details.branches =
    branches.length > 0
      ? `${branches.join(', ')} et leurs protections`
      : 'branches permanentes et leurs protections';
  if (groups) details['lldap-groups'] = `${groups.readers}, ${groups.editors}`;
  details['appenv-overlays'] =
    branches.length > 0 ? `appenv ${frenchList(branches)}` : 'appenv';
  return {
    phases: PHASES,
    names: CREATION_STEP_NAMES,
    details,
    parts: CREATION_PARTS,
  };
}

/**
 * What the running step does, for « En cours : … », `appTitle` the
 * application's name (`taskTemplate.ts`; Odoo's by default). Undefined for a
 * step this page does not know. `standard`: the project's type has the
 * `standard` profile (point 8), no image of its own to prepare.
 */
export function runningExplanation(
  stepId: string,
  facts: CreationFacts | undefined,
  appTitle: string = LEGACY_APP_TITLE,
  standard = false,
): string | undefined {
  const project = facts?.project;
  const groups = project ? projectGroups(project) : undefined;
  const branches = facts?.permanentBranches ?? [];
  switch (stepId) {
    case 'check-name':
      return `Vérification que l’identifiant${
        project ? ` ${project}` : ''
      } n’est pris ni dans GitLab, ni dans le catalogue, ni par un namespace. Rien n’est encore créé.`;
    case 'fetch':
      return standard
        ? 'Préparation du code du projet à partir de vos réponses : chart Helm et fiche du catalogue.'
        : `Préparation du code du projet à partir de vos réponses : image ${appTitle}, chart Helm et fiche du catalogue.`;
    case 'publish':
      return `Création du dépôt${
        facts?.repoPath ? ` ${facts.repoPath}` : ''
      } dans GitLab et premier commit du projet.`;
    case 'branches':
      return `Création des branches permanentes${
        branches.length > 0 ? ` ${frenchList(branches)}` : ''
      } et pose de leurs protections.`;
    case 'gitlab-member':
      return 'Invitation de votre compte comme Developer du dépôt, à l’adresse e-mail de votre fiche Backstage.';
    case 'lldap-groups':
      return `Création de ${
        groups ? `${groups.readers} et ${groups.editors}` : 'groupes du projet'
      }, puis ajout de votre compte. Elle peut prendre une minute ou plus.`;
    case 'appenv-overlays':
      return `Préparation des environnements permanents${
        branches.length > 0 ? ` ${frenchList(branches)}` : ''
      } : namespace, projet ArgoCD, sauvegardes.`;
    case 'appenv-ephemeral':
      return 'Préparation du modèle des environnements éphémères : chaque branche dev-* aura le sien.';
    case 'appenv-publish':
      return 'Publication du dépôt des environnements, qu’ArgoCD prend ensuite en charge.';
    default:
      return undefined;
  }
}

/**
 * What the `gitlab-member` step did, read in its log: the action
 * (`gitlabProjectInviteCreator.ts`) never fails the task, and says the
 * outcome only in its log and in its `status` output, which the event
 * stream does not carry. `member` and `pending` grant the access (now, or
 * once the invitation is accepted); `refused` did not; `unknown` when its
 * log says none of it.
 */
export type InviteOutcome = 'member' | 'pending' | 'refused' | 'unknown';

export function inviteOutcome(lines: string[] | undefined): InviteOutcome {
  const log = (lines ?? []).join('\n');
  if (
    log.includes('Accès GitLab non accordé automatiquement') ||
    log.includes('Aucune invitation envoyée')
  ) {
    return 'refused';
  }
  if (
    log.includes('Invitation en attente pour') ||
    log.includes('envoyée, mais son état n’a pas pu être lu')
  ) {
    return 'pending';
  }
  if (log.includes(' est Developer du dépôt ')) return 'member';
  return 'unknown';
}

/**
 * The one permanent environment the `odoo-project` template deploys: the
 * appenv skeleton (platform-catalog `scaffolder/appenv/skeleton/odoo`) puts
 * `.active` under a folder named
 * `${{ values.environment if values.environment == 'dev' }}`, empty — and
 * skipped — for any other environment.
 */
export const ACTIVE_AT_CREATION = 'dev';

/** The icon of a row, as `DeletionReport`'s (`RowIcon`) and a few more. */
export type CreatedIcon =
  | 'gitlab'
  | 'argocd'
  | 'branch'
  | 'lldap'
  | 'ephemeral'
  | 'catalog';

export interface CreatedItem {
  value: string;
  note?: string;
}

export interface CreatedRow {
  key: string;
  element: string;
  icon: CreatedIcon;
  items: CreatedItem[];
}

/**
 * « Ce qui a été créé »: one row per thing the task made, each only when the
 * step that makes it is done — `states` étant ceux des lignes de
 * `stepPhases`, une étape et ses parties (`CREATION_PARTS`) toutes faites —
 * and its values are known — from the
 * parameters, the step inputs of the spec, the outputs. A step failed,
 * skipped or not run yet gives no row.
 *
 * The namespaces and the ApplicationSet are named by the appenv skeleton
 * (platform-catalog `scaffolder/appenv/skeleton/odoo`): its overlay patches
 * the Namespace to `prj-<projet>-<branche>`, and the dev overlay holds the
 * ApplicationSet `prj-<projet>-ephemeral`.
 */
export function createdRows(
  facts: CreationFacts,
  states: Readonly<Record<string, StepState>>,
  invite: InviteOutcome,
): CreatedRow[] {
  const done = (...ids: CreationStepId[]) =>
    ids.every(id => states[id] === 'done');
  const { project } = facts;
  const rows: CreatedRow[] = [];

  if (done('publish') && facts.repoPath) {
    rows.push({
      key: 'repository',
      element: 'Dépôt du projet',
      icon: 'gitlab',
      items: [{ value: facts.repoPath }],
    });
  }
  if (done('branches') && facts.permanentBranches.length > 0) {
    rows.push({
      key: 'branches',
      element: 'Branches',
      icon: 'branch',
      items: facts.permanentBranches.map(branch => ({
        value: branch,
        note: facts.developerBranches.includes(branch)
          ? 'protégée, Developers'
          : 'protégée, mainteneurs',
      })),
    });
  }
  if (done('gitlab-member') && (invite === 'member' || invite === 'pending')) {
    let note = facts.user;
    if (invite === 'pending') note = 'invitation en attente';
    rows.push({
      key: 'access',
      element: 'Votre accès',
      icon: 'gitlab',
      items: [{ value: 'Developer', ...(note ? { note } : {}) }],
    });
  }
  if (done('lldap-groups')) {
    const groups = projectGroups(project);
    rows.push({
      key: 'groups',
      element: 'Groupes LLDAP',
      icon: 'lldap',
      items: [
        { value: groups.readers, note: 'lecture' },
        { value: groups.editors, note: 'édition' },
      ],
    });
  }
  if (
    done('appenv-overlays', 'appenv-publish') &&
    facts.permanentBranches.length > 0
  ) {
    // Only `dev` is deployed at creation: the appenv skeleton writes the
    // `.active` marker for it alone, and every other permanent environment
    // is prepared (its folder, its values.yaml) and left asleep.
    rows.push({
      key: 'environments',
      element: 'Environnements',
      icon: 'argocd',
      items: facts.permanentBranches.map(branch => ({
        value: `prj-${project}-${branch}`,
        note: branch === ACTIVE_AT_CREATION ? 'déployé' : 'préparé, en sommeil',
      })),
    });
  }
  if (
    done('appenv-overlays', 'appenv-ephemeral', 'appenv-publish') &&
    facts.permanentBranches.includes('dev')
  ) {
    rows.push({
      key: 'ephemeral',
      element: 'Branches éphémères',
      icon: 'ephemeral',
      items: [{ value: `ApplicationSet prj-${project}-ephemeral` }],
    });
  }
  if (done('publish') && facts.entityRef) {
    rows.push({
      key: 'catalog',
      element: 'Catalogue',
      icon: 'catalog',
      items: [{ value: facts.entityRef }],
    });
  }
  return rows;
}

/**
 * When the task ended and how long it ran, from its steps' times in the
 * stream: the first start to the last end. Undefined when no step reported
 * both.
 */
export function taskTimes(streamed: Record<string, ScaffolderStep>): {
  endedAt?: number;
  durationMs?: number;
} {
  const starts: number[] = [];
  const ends: number[] = [];
  for (const step of Object.values(streamed)) {
    const started = step.startedAt ? Date.parse(step.startedAt) : NaN;
    const ended = step.endedAt ? Date.parse(step.endedAt) : NaN;
    if (Number.isFinite(started)) starts.push(started);
    if (Number.isFinite(ended)) ends.push(ended);
  }
  if (ends.length === 0) return {};
  const endedAt = Math.max(...ends);
  if (starts.length === 0) return { endedAt };
  return { endedAt, durationMs: Math.max(0, endedAt - Math.min(...starts)) };
}
